import { logger } from "@/lib/logger";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Menu, X, LogOut, User, Settings, Stethoscope, Building2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import logoImage from "/lovable-uploads/1287e66a-7a3e-48a7-ac45-380a5079191a.png";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { performCompleteLogout } from '@/lib/auth-utils';
import { useToast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import { useNavigationItems, formatCategoryName } from '@/hooks/useNavigationItems';
import { useUserContexts } from '@/hooks/useUserContexts';
import { ContextSwitcher } from '@/components/navigation/ContextSwitcher';
import { useSiteContext } from '@/contexts/SiteContext';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';

interface MobileHeaderProps {
  showMenu?: boolean;
}

export function MobileHeader({ showMenu = true }: MobileHeaderProps) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const siteContext = useSiteContext();
  const { user, userProfile, isAdmin, primaryRole, isAuthReady } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const prevPathname = useRef(location.pathname);
  const { activeContext, contexts } = useUserContexts();
  const navigationStructure = useNavigationItems(
    userProfile?.role, 
    isAdmin, 
    activeContext,
    contexts
  );

  // Track route transitions to prevent phantom menu flash
  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      setIsTransitioning(true);
      prevPathname.current = location.pathname;
      
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 150);
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Role-aware home destination
  const effectiveRole = (primaryRole || userProfile?.role || '').toLowerCase();
  const homeHref = (isAdmin || effectiveRole === 'admin' || effectiveRole === 'super_admin')
    ? '/admin/command-center'
    : (effectiveRole === 'sponsor' || effectiveRole === 'sponsor_service_provider')
      ? '/sponsor/command-center'
      : (effectiveRole === 'cro')
        ? '/cro/command-center'
        : (effectiveRole === 'trial_site' || effectiveRole === 'medical_professional' || effectiveRole === 'healthcare_provider')
          ? '/site-dashboard'
          : (user ? '/patient-dashboard' : '/home');

  const getInitials = () => {
    if (userProfile?.first_name && userProfile?.last_name) {
      return `${userProfile.first_name[0]}${userProfile.last_name[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const handleSignOut = async () => {
    if (isLoggingOut) return;
    
    setIsLoggingOut(true);
    
    try {
      const { success, error } = await performCompleteLogout();
      
      if (!success) {
        toast({
          title: "Error signing out",
          description: error || "Failed to sign out properly",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Signed out successfully",
          description: "You have been signed out of your account.",
        });
      }
      
      navigate("/auth", { replace: true });
      setTimeout(() => {
        window.location.replace("/auth");
      }, 100);
    } catch (error) {
      logger.error("Error during logout:", error);
      navigate("/auth", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Wait for auth AND site context to be ready before rendering
  const isReady = isAuthReady && !siteContext?.isLoading;

  if (!isReady) {
    return (
      <header className="md:hidden sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="h-10 w-24 bg-muted/50 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-8 w-8 bg-muted/50 rounded-full animate-pulse" />
            <div className="h-8 w-8 bg-muted/50 rounded animate-pulse" />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="md:hidden sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className={`flex items-center justify-between px-4 h-16 transition-opacity duration-150 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        {/* Logo with trial site distinction */}
        <div className="flex items-center gap-2">
          <Link to={homeHref} className="flex items-center">
            <img 
              src={siteContext?.site?.logo_url || logoImage} 
              alt={siteContext?.site?.display_name || "Pharma Trial Connect"} 
              className="h-10 w-auto"
            />
          </Link>
          {/* "Powered by PTC BRIDGE™" badge for trial sites on mobile */}
          {siteContext?.site && (
            <span className="text-[9px] text-muted-foreground border border-border rounded-full px-1.5 py-0.5">
              PTC
            </span>
          )}
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {/* Language Switcher */}
          <LanguageSwitcher />
          {/* Context Indicator Badge */}
          {user && contexts.length > 1 && activeContext && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
              {activeContext === 'professional' ? (
                <>
                  <Stethoscope className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-medium text-primary">Professional</span>
                </>
              ) : (
                <>
                  <Building2 className="h-3.5 w-3.5 text-secondary" />
                  <span className="text-xs font-medium text-secondary">Site</span>
                </>
              )}
            </div>
          )}

          {user && userProfile && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-10 w-10 rounded-full min-touch-target p-0"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={userProfile.avatar_url || ''} />
                    <AvatarFallback className="text-xs">{getInitials()}</AvatarFallback>
                  </Avatar>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" style={{ zIndex: 60 }}>
              <DropdownMenuLabel>{t('navigation.profile')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer flex items-center">
                    <User className="mr-2 h-4 w-4" />
                    {t('navigation.profile')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer flex items-center">
                    <Settings className="mr-2 h-4 w-4" />
                    {t('navigation.settings')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleSignOut} 
                  className="text-destructive cursor-pointer"
                  disabled={isLoggingOut}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {isLoggingOut ? t('common.loading') : t('navigation.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {showMenu && (
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="min-touch-target"
                  aria-label="Open menu"
                >
                  {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm overflow-y-auto">
                <nav className="flex flex-col gap-2 mt-6">
                  {/* Context Switcher for multi-role users */}
                  {user && contexts.length > 1 && (
                    <div className="mb-4">
                      <ContextSwitcher />
                      <Separator className="mt-4" />
                    </div>
                  )}

                  {/* Dynamic Navigation based on role */}
                  {Object.entries(navigationStructure).map(([category, items]) => (
                    <div key={category} className="mb-2">
                      <h3 className="text-xs font-semibold uppercase text-muted-foreground px-3 py-2 tracking-wider">
                        {formatCategoryName(category)}
                      </h3>
                      <div className="flex flex-col gap-1">
                        {items.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.href}
                              to={item.href}
                              className="flex items-center gap-3 px-3 py-3 text-base font-medium hover:bg-muted rounded-lg min-h-[44px] transition-colors"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <Icon className="h-5 w-5 flex-shrink-0" />
                              <span>{item.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                      <Separator className="mt-2" />
                    </div>
                  ))}

                  {/* Auth Links for non-authenticated users */}
                  {!user && (
                    <div className="mt-4 flex flex-col gap-2">
                      <Link 
                        to="/register" 
                        className="w-full"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Button variant="default" className="w-full min-h-[44px]">
                          {t('navigation.register')}
                        </Button>
                      </Link>
                      <Link 
                        to="/auth" 
                        className="w-full"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Button variant="outline" className="w-full min-h-[44px]">
                          {t('navigation.login')}
                        </Button>
                      </Link>
                    </div>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  );
}
